import { DocumentationMarkdown, documentationHeadings } from "@sylviq/ui-react-web/components/docs/DocumentationMarkdown";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { useMemo, useState } from "react";
import publicDocs from "@/generated/public-docs.json";
import { useDocumentMeta } from "@/lib/document-meta";
import { DocsShell } from "./DocsShell";

export interface PublicDocument {
  id: string;
  title: string;
  audienceRole: "product-help" | "extension-coding";
  content: string;
}

const documents = publicDocs.documents as PublicDocument[];

const routeFor = (document: PublicDocument) =>
  document.audienceRole === "extension-coding"
    ? `/docs/sdk/${document.id}`
    : `/docs/${document.id}`;

function navigationFor(allDocuments: readonly PublicDocument[]) {
  const docsWithHref = allDocuments.map((document) => ({
    label: document.title.replace(/`/g, ""),
    href: routeFor(document),
    document,
  }));
  const group = (label: string, items: readonly { label: string; href: string }[]) => ({
    label,
    items: items.map(({ label: itemLabel, href }) => ({ label: itemLabel, href })),
  });

  return [
    group("Start here", [{ label: "Documentation", href: "/docs" }]),
    group("Console", docsWithHref.filter(({ document }) => document.id.startsWith("console-"))),
    group("Accounting", docsWithHref.filter(({ document }) => document.id.startsWith("accounting-"))),
    group("Extension authoring", docsWithHref.filter(({ document }) =>
      document.audienceRole === "extension-coding" && !document.id.startsWith("reference/"))),
    group("SDK reference", docsWithHref.filter(({ document }) => document.id.startsWith("reference/"))),
  ];
}

const safeDocumentationHref = (href: string) =>
  href.startsWith("#")
  || href === "/docs"
  || href.startsWith("/docs/")
  || href === "https://www.sylviq.io"
  || href.startsWith("https://www.sylviq.io/")
  || href === "https://docs.sylviq.io"
  || href.startsWith("https://docs.sylviq.io/")
  || /^https?:\/\//i.test(href)
  || /^mailto:/i.test(href);

export function MarkdownContent({ content }: { content: string }) {
  return <DocumentationMarkdown content={content} isAllowedHref={safeDocumentationHref} />;
}

export function DocsIndexContent({ documents: allDocuments }: { documents: PublicDocument[] }) {
  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    return allDocuments.filter((document) => {
      const haystack = `${document.title} ${document.content} ${document.audienceRole}`.toLocaleLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  }, [allDocuments, query]);

  return (
    <>
      <label className="mb-6 block max-w-xl">
        <span className="mb-2 block text-sm font-medium">Search documentation</span>
        <Input
          type="search"
          aria-label="Search documentation"
          placeholder="Search guides and references"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
      </label>
      <p className="mb-4 text-sm text-[var(--muted-foreground)]" aria-live="polite">
        {visible.length} {visible.length === 1 ? "page" : "pages"}
      </p>
      {visible.length ? (
        <ul className="grid list-none gap-3 p-0 sm:grid-cols-2">
          {visible.map((document) => (
            <li key={document.id}>
              <a href={routeFor(document)} className="surface block h-full rounded-xl border p-5 hover:border-[var(--action)]">
                <p className="text-xs font-semibold tracking-wide text-[var(--muted-foreground)] uppercase">
                  {document.audienceRole === "product-help" ? "Product help" : "Extension development"}
                </p>
                <h2 className="mt-2 text-lg font-semibold">{document.title}</h2>
                <p className="mt-2 line-clamp-3 text-sm text-[var(--muted-foreground)]">
                  {document.content
                    .replace(/<!--[\s\S]*?-->/g, " ")
                    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
                    .replace(/^\s*```[^\n]*|```\s*$/gm, " ")
                    .replace(/^\s*#{1,6}\s*/gm, "")
                    .replace(/[`*_]/g, "")
                    .replace(/\s+/g, " ")
                    .trim()
                    .slice(0, 220)}
                </p>
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-xl border border-dashed p-6 text-sm text-[var(--muted-foreground)]">
          No documentation pages match that search. Try a different word.
        </p>
      )}
    </>
  );
}

export function DocsIndexPage() {
  useDocumentMeta({
    title: "SYLVIQ Documentation",
    description: "SYLVIQ product guides and extension development documentation.",
    path: "/docs",
  });
  return (
    <DocsShell navigation={navigationFor(documents)} activeHref="/docs">
      <p className="text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">Documentation</p>
      <h1 className="mt-3 text-4xl font-semibold tracking-tight">Guides and references</h1>
      <p className="mb-8 mt-3 max-w-2xl text-[var(--muted-foreground)]">
        Find help using SYLVIQ or browse the extension authoring reference.
      </p>
      <DocsIndexContent documents={documents} />
    </DocsShell>
  );
}

export function DocsDocumentPage({
  id,
  audienceRole,
}: {
  id: string;
  audienceRole?: PublicDocument["audienceRole"];
}) {
  const document = documents.find(
    (candidate) =>
      candidate.id === id && (!audienceRole || candidate.audienceRole === audienceRole),
  );
  useDocumentMeta({
    title: document ? `${document.title} | SYLVIQ Docs` : "Page not found | SYLVIQ Docs",
    description: document?.content.replace(/[#*_`]/g, " ").slice(0, 150) ?? "This documentation page does not exist.",
    path: document ? routeFor(document) : "/docs",
  });
  const headings = document ? documentationHeadings(document.content) : [];
  return (
    <DocsShell
      navigation={navigationFor(documents)}
      activeHref={document ? routeFor(document) : "/docs"}
      headings={headings}
    >
      {document ? (
        <>
          <a href="/docs" className="text-sm text-[var(--action)] underline underline-offset-4">All documentation</a>
          <p className="mt-7 text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
            {document.audienceRole === "product-help" ? "Product help" : "Extension development"}
          </p>
          <h1 className="mb-8 mt-2 text-4xl font-semibold tracking-tight">{document.title}</h1>
          {document.audienceRole === "extension-coding" ? (
            <p className="mb-6 rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4 text-sm text-[var(--muted-foreground)]">
              This page reproduces the SDK authoring documentation. A target host may support a narrower set of active capabilities; confirm compatibility with the host you use.
            </p>
          ) : null}
          <MarkdownContent content={document.content} />
        </>
      ) : (
        <>
          <h1 className="text-3xl font-semibold">Documentation page not found</h1>
          <p className="mt-3 text-[var(--muted-foreground)]">That page is not part of the published documentation.</p>
          <a href="/docs" className="mt-5 inline-block text-[var(--action)] underline">Browse documentation</a>
        </>
      )}
    </DocsShell>
  );
}
